import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangaList } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const manga = useMemo(() => mangaList.find((m) => m.slug === slug), [slug]);

  const relatedManga = useMemo(() => {
    if (!manga) return [];
    return mangaList
      .filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t))))
      .slice(0, 6);
  }, [manga]);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h1 className="text-xl font-heading text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${manga.title} | 亲亲漫画${manga.category}免费全集下拉式在线阅读`;
  const pageDescription = `${manga.title} - ${manga.description.substring(0, 120)} 亲亲漫画${manga.category}免费全集下拉式在线阅读，每日更新。`;
  const pageKeywords = `亲亲漫画,${manga.title},${manga.category},${manga.tags.join(',')},免费漫画,在线阅读,全集漫画,下拉式`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": manga.title,
    "author": {
      "@type": "Person",
      "name": manga.author,
    },
    "description": manga.description,
    "datePublished": "2026-01-01",
    "dateModified": manga.updatedAt,
    "image": manga.coverImage,
    "publisher": {
      "@type": "Organization",
      "name": "亲亲漫画",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://qinqinmanga.com/" },
      { "@type": "ListItem", "position": 2, "name": manga.category, "item": `https://qinqinmanga.com/category/${manga.category === '韩漫' ? 'hanman' : manga.category === '日漫' ? 'riman' : 'guoman'}` },
      { "@type": "ListItem", "position": 3, "name": manga.title },
    ],
  };

  const imageSchema = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": manga.coverImage,
    "name": manga.title,
    "caption": `${manga.title} - 亲亲漫画${manga.category}免费全集下拉式在线阅读`,
  };

  return (
    <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(imageSchema) }}
    />
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: manga.category, href: `/category/${manga.category === '韩漫' ? 'hanman' : manga.category === '日漫' ? 'riman' : 'guoman'}` },
              { label: manga.title },
            ]}
          />

          <article className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] md:sticky md:top-24">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} - 亲亲漫画${manga.category}免费全集下拉式在线阅读`}
                    title={`${manga.title} - 亲亲漫画${manga.category}免费全集下拉式在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="text-xs font-medium text-white bg-accent-500/90 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {manga.category}
                    </span>
                  </div>
                  {manga.status === '已完结' && (
                    <div className="absolute top-3 right-3">
                      <span className="text-xs font-medium text-white bg-secondary-500/90 px-2.5 py-1 rounded-full whitespace-nowrap">
                        已完结
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-user-line text-foreground-400 text-xs"></i>
                    </span>
                    <span className="text-sm text-foreground-600">{manga.author}</span>
                  </div>
                  <span className="text-foreground-300">·</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </span>
                    <span className="text-sm font-semibold text-foreground-800">{manga.rating}</span>
                  </div>
                  <span className="text-foreground-300">·</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-eye-line text-foreground-400 text-xs"></i>
                    </span>
                    <span className="text-sm text-foreground-600">{formatViews(manga.views)}次阅读</span>
                  </div>
                  <span className="text-foreground-300">·</span>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${manga.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'}`}>
                    {manga.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
                </div>

                <div>
                  <h2 className="text-base font-heading text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-list-check text-primary-500"></i>
                    章节列表
                    <span className="text-xs font-normal text-foreground-400">(共{manga.chapters.length}话)</span>
                  </h2>
                  <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                    {manga.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className="ri-file-text-line text-xs"></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 truncate">
                            {chapter.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 flex-shrink-0 ml-3">{chapter.updatedAt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {relatedManga.length > 0 && (
            <section className="py-8 md:py-10 border-t border-background-200/70">
              <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-5">
                <i className="ri-links-line text-primary-500 mr-2"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedManga.map((rm) => (
                  <MangaCard key={rm.id} manga={rm} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
}